import { memo, useMemo } from "react";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import Typography from "@mui/material/Typography";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import "dayjs/locale/pt-br";

import {
  getAppointmentPeriodSectionTitle,
  isPatientFinderAppointmentMode,
  PATIENT_FINDER_APPOINTMENT_MODES,
  type PatientFinderAppointmentMode,
  shouldLimitPeriodToToday,
} from "../helpers/patientFinderAppointmentMode";
import CommunicationsSegmentedFilterControl from "./CommunicationsSegmentedFilterControl";

export interface CommunicationsAppointmentPeriodFieldsProps {
  appointmentMode: PatientFinderAppointmentMode;
  onAppointmentModeChange: (mode: PatientFinderAppointmentMode) => void;
  periodFrom: Dayjs | null;
  periodTo: Dayjs | null;
  onPeriodFromChange: (value: Dayjs | null) => void;
  onPeriodToChange: (value: Dayjs | null) => void;
  periodLabel: string;
}

/**
 * Tipo de consulta (realizada / não realizada / agendada) + período por dia.
 */
const CommunicationsAppointmentPeriodFields = memo(
  ({
    appointmentMode,
    onAppointmentModeChange,
    periodFrom,
    periodTo,
    onPeriodFromChange,
    onPeriodToChange,
    periodLabel,
  }: CommunicationsAppointmentPeriodFieldsProps) => {
    const modeMeta = useMemo(
      () =>
        PATIENT_FINDER_APPOINTMENT_MODES.find(
          (m) => m.value === appointmentMode,
        ),
      [appointmentMode],
    );

    const limitToToday = shouldLimitPeriodToToday(appointmentMode);
    const isScheduled = appointmentMode === "scheduled";
    const today = dayjs().startOf("day");

    return (
      <Stack spacing={1.5}>
        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            Filtrar pacientes
          </Typography>
          <CommunicationsSegmentedFilterControl
            value={appointmentMode}
            onChange={(_, value: string | null) => {
              if (value != null && isPatientFinderAppointmentMode(value)) {
                onAppointmentModeChange(value);
              }
            }}
            aria-label="Tipo de busca por consulta"
          >
            {PATIENT_FINDER_APPOINTMENT_MODES.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </CommunicationsSegmentedFilterControl>
          {modeMeta ? (
            <Typography variant="caption" color="text.secondary">
              {modeMeta.shortDescription} Período: {periodLabel}.
            </Typography>
          ) : null}
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            {getAppointmentPeriodSectionTitle(appointmentMode)}
          </Typography>

          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="pt-br"
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <DatePicker
                label="A partir de (opcional)"
                value={periodFrom}
                onChange={onPeriodFromChange}
                format="DD/MM/YYYY"
                minDate={isScheduled ? today : undefined}
                maxDate={limitToToday ? today : undefined}
                slotProps={{
                  textField: { size: "medium", fullWidth: true },
                }}
              />
              {!isScheduled ? (
                <DatePicker
                  label="Até (opcional)"
                  value={periodTo}
                  onChange={onPeriodToChange}
                  format="DD/MM/YYYY"
                  maxDate={limitToToday ? today : undefined}
                  slotProps={{
                    textField: { size: "medium", fullWidth: true },
                  }}
                />
              ) : null}
            </Stack>
          </LocalizationProvider>
        </Stack>
      </Stack>
    );
  },
);

CommunicationsAppointmentPeriodFields.displayName =
  "CommunicationsAppointmentPeriodFields";

export default CommunicationsAppointmentPeriodFields;
